import { useCallback, useEffect, useMemo, useState } from 'react'
import siteMap from '../../../siteMap'
import { useSelector } from 'react-redux'
import { RootState } from '../../../store'
import { Alert, Button, Chip, Grid, Stack, Typography, useTheme } from '@mui/material'
import { usePortfolioClimateData } from '../../../hooks/climateData'
import { EmissionTier, MetricState, CanonicalPeriod } from '../common'
import { ExtremeWindScoreBadge, scoreColor as extremeWindScorePalette } from '../common/extremeWind/utils'
import { useHistory } from 'react-router-dom'
import { useCurrentUser } from '../../../hooks/users'
import { LoadingValue } from './PortfolioLoading'
import PortfolioKpiValueMotion from './PortfolioKpiValueMotion'
import PortfolioSummaryMetricCard from './PortfolioSummaryMetricCard'
import PortfolioPropertyBreakdownTable, { type PortfolioMetricSpec } from './PortfolioPropertyBreakdownTable'
import PortfolioPropertyMetricMap, {
  type PortfolioMapColorOption,
  type PortfolioMapRadiusOption,
  type NumericMetricSpec,
  type PopupFieldSpec,
} from './PortfolioPropertyMetricMap'
import { useLocations } from '../../../hooks/locations'

function compactNumber(n: number) {
  return new Intl.NumberFormat(undefined, { notation: 'compact', maximumFractionDigits: 1 }).format(n)
}

function fixed1(n: number) {
  return new Intl.NumberFormat(undefined, { maximumFractionDigits: 1 }).format(n)
}

function pickScalarMetric(entry: any, tierKey: string): number | null {
  if (!entry || entry.state?.value !== MetricState.SUCCEEDED || !entry.data) return null
  const node = entry.data?.data?.[tierKey]
  const raw = typeof node?.y !== 'undefined' ? node.y : node
  const n = typeof raw === 'number' ? raw : typeof raw === 'string' ? parseFloat(raw) : NaN
  return Number.isFinite(n) ? n : null
}

type PropertySubset = { mode: 'all' } | { mode: 'selected'; ids: number[] }

type ScalarMetricMap = {
  values: Record<number, number | null>
  status: Record<number, MetricState | null>
}

export default function PortfolioExtremeWind({
  emissionTier,
  selectedPeriod,
}: {
  emissionTier: EmissionTier
  selectedPeriod: CanonicalPeriod
}) {
  const theme = useTheme()
  const history = useHistory()
  const { groupId } = useSelector((state: RootState) => state.system)
  const { data: locations } = useLocations({ group_id: groupId ?? undefined })
  const numberOfProperties = locations?.length ?? null
  const { data: scoreByProperty, isLoading: scoreLoading } = usePortfolioClimateData('tc:score', {
    groupId: groupId ?? undefined,
  })
  const { data: lossByProperty, isLoading: lossLoading } = usePortfolioClimateData('tc:average_damage_loss', {
    groupId: groupId ?? undefined,
  })
  const { data: biByProperty, isLoading: biLoading } = usePortfolioClimateData('tc:average_bi_days', {
    groupId: groupId ?? undefined,
  })
  const { currencySymbol } = useCurrentUser()

  const goToProperty = useCallback(
    (propertyId: number) => history.push(siteMap.PropertyClimateAnalytics.getPath(propertyId, 'tc', emissionTier)),
    [emissionTier, history],
  )

  const [propertySubset, setPropertySubset] = useState<PropertySubset>({ mode: 'all' })
  const [mapClearSelectionToken, setMapClearSelectionToken] = useState(0)

  useEffect(() => {
    setPropertySubset({ mode: 'all' })
    setMapClearSelectionToken(v => v + 1)
  }, [groupId])

  const activePropertyIds = useMemo(() => {
    return propertySubset.mode === 'selected' ? propertySubset.ids : undefined
  }, [propertySubset])

  const handleConfirmSelection = useCallback((propertyIds: number[]) => {
    if (!propertyIds.length) {
      setPropertySubset({ mode: 'all' })
      return
    }
    setPropertySubset({ mode: 'selected', ids: propertyIds })
  }, [])

  const handleClearSelection = useCallback(() => {
    setPropertySubset({ mode: 'all' })
  }, [])

  const clearActiveSubset = useCallback(() => {
    setPropertySubset({ mode: 'all' })
    setMapClearSelectionToken(v => v + 1)
  }, [])

  const tierKey = emissionTier as unknown as string

  const scoreMetric = useMemo<ScalarMetricMap>(() => {
    const values: Record<number, number | null> = {}
    const status: Record<number, MetricState | null> = {}
    if (scoreByProperty && typeof scoreByProperty === 'object') {
      Object.values(scoreByProperty as Record<string, any>).forEach(entry => {
        const propertyId = entry?.property_id
        if (typeof propertyId !== 'number') return
        status[propertyId] = entry.state?.value ?? null
        values[propertyId] = pickScalarMetric(entry, tierKey)
      })
    }
    return { values, status }
  }, [scoreByProperty, tierKey])

  const lossMetric = useMemo<ScalarMetricMap>(() => {
    const values: Record<number, number | null> = {}
    const status: Record<number, MetricState | null> = {}
    if (lossByProperty && typeof lossByProperty === 'object') {
      Object.values(lossByProperty as Record<string, any>).forEach(entry => {
        const propertyId = entry?.property_id
        if (typeof propertyId !== 'number') return
        status[propertyId] = entry.state?.value ?? null
        values[propertyId] = pickScalarMetric(entry, tierKey)
      })
    }
    return { values, status }
  }, [lossByProperty, tierKey])

  const biMetric = useMemo<ScalarMetricMap>(() => {
    const values: Record<number, number | null> = {}
    const status: Record<number, MetricState | null> = {}
    if (biByProperty && typeof biByProperty === 'object') {
      Object.values(biByProperty as Record<string, any>).forEach(entry => {
        const propertyId = entry?.property_id
        if (typeof propertyId !== 'number') return
        status[propertyId] = entry.state?.value ?? null
        values[propertyId] = pickScalarMetric(entry, tierKey)
      })
    }
    return { values, status }
  }, [biByProperty, tierKey])

  const kpis = useMemo(() => {
    const collect = (metricValues: Record<number, number | null>) => {
      if (activePropertyIds) {
        return activePropertyIds.map(id => metricValues[id]).filter((v): v is number => typeof v === 'number')
      }
      return Object.values(metricValues).filter((v): v is number => typeof v === 'number')
    }
    const scoreValues = collect(scoreMetric.values)
    const lossValues = collect(lossMetric.values)
    const biValues = collect(biMetric.values)
    const mean = (xs: number[]) => (xs.length ? xs.reduce((a, b) => a + b, 0) / xs.length : null)
    const sum = (xs: number[]) => (xs.length ? xs.reduce((a, b) => a + b, 0) : null)

    return {
      avgScore: mean(scoreValues),
      totalLoss: sum(lossValues),
      avgBiDays: mean(biValues),
    }
  }, [activePropertyIds, biMetric.values, lossMetric.values, scoreMetric.values])

  const tableMetrics = useMemo<PortfolioMetricSpec[]>(
    () => [
      {
        kind: 'score',
        key: 'score',
        label: 'Risk Rating',
        width: 150,
        loading: scoreLoading,
        getValue: propertyId => scoreMetric.values[propertyId] ?? null,
        renderScore: value => <ExtremeWindScoreBadge score={value} size="dense" />,
        status: {
          key: 'statusScore',
          label: 'Status (Risk Rating)',
          hiddenByDefault: true,
          loading: scoreLoading,
          getValue: propertyId => scoreMetric.status[propertyId] ?? null,
        },
      },
      {
        kind: 'currency',
        key: 'loss',
        label: 'Damage Loss',
        width: 170,
        loading: lossLoading,
        suffix: '/ year',
        getValue: propertyId => lossMetric.values[propertyId] ?? null,
        status: {
          key: 'statusLoss',
          label: 'Status (Damage Loss)',
          hiddenByDefault: true,
          loading: lossLoading,
          getValue: propertyId => lossMetric.status[propertyId] ?? null,
        },
      },
      {
        kind: 'number',
        key: 'bi',
        label: 'Business Interruption',
        width: 170,
        loading: biLoading,
        format: 'fixed1',
        suffix: 'days/year',
        getValue: propertyId => biMetric.values[propertyId] ?? null,
        status: {
          key: 'statusBI',
          label: 'Status (Business Interruption)',
          hiddenByDefault: true,
          loading: biLoading,
          getValue: propertyId => biMetric.status[propertyId] ?? null,
        },
      },
    ],
    [biLoading, biMetric.status, biMetric.values, lossLoading, lossMetric.status, lossMetric.values, scoreLoading, scoreMetric.status, scoreMetric.values],
  )

  const scoreNumericMetric = useMemo<NumericMetricSpec>(
    () => ({
      key: 'score',
      label: 'Risk Rating',
      loading: scoreLoading,
      getValue: propertyId => scoreMetric.values[propertyId] ?? null,
      renderValue: value => <ExtremeWindScoreBadge score={value} size="dense" />,
    }),
    [scoreLoading, scoreMetric.values],
  )

  const lossNumericMetric = useMemo<NumericMetricSpec>(
    () => ({
      key: 'loss',
      label: 'Damage Loss',
      loading: lossLoading,
      formatKind: 'currency',
      suffix: '/ year',
      getValue: propertyId => lossMetric.values[propertyId] ?? null,
    }),
    [lossLoading, lossMetric.values],
  )

  const mapColorOptions = useMemo<PortfolioMapColorOption[]>(
    () => [
      {
        key: 'score',
        label: 'Risk Rating',
        type: 'discrete',
        metric: scoreNumericMetric,
        palette: extremeWindScorePalette,
        labels: {
          1: 'Very Low',
          2: 'Low',
          3: 'Moderate',
          4: 'High',
          5: 'Very High',
        },
        normalize: v => Math.min(Math.max(Math.round(v), 1), 5),
      },
      {
        key: 'loss',
        label: 'Damage Loss',
        type: 'continuous',
        metric: lossNumericMetric,
        domain: { type: 'auto', clampPercentiles: [5, 95] },
        gradient: {
          stops: [
            { offset: 0, color: '#2ecc71' },
            { offset: 0.5, color: '#f1c40f' },
            { offset: 1, color: '#e74c3c' },
          ],
        },
      },
    ],
    [lossNumericMetric, scoreNumericMetric],
  )

  const mapRadiusOptions = useMemo<PortfolioMapRadiusOption[]>(
    () => [
      {
        key: 'loss',
        label: 'Damage Loss',
        type: 'scaled',
        metric: lossNumericMetric,
        scale: 'sqrt',
        domain: { type: 'auto', clampPercentiles: [5, 95] },
        rangePx: { min: 6, max: 24 },
      },
    ],
    [lossNumericMetric],
  )

  const mapPopupFields = useMemo<PopupFieldSpec[]>(
    () => [
      {
        key: 'bi',
        label: 'Business Interruption',
        loading: biLoading,
        decimals: 1,
        suffix: 'days/year',
        getValue: propertyId => biMetric.values[propertyId] ?? null,
      },
    ],
    [biLoading, biMetric.values],
  )

  const kpiMotionKey = `${emissionTier}-${selectedPeriod}`

  return (
    <Stack alignItems={'center'} pb={6}>
      <Stack sx={{ maxWidth: theme.breakpoints.values?.['lg'], width: '100%' }} spacing={2}>
        {activePropertyIds && (
          <Alert
            severity="info"
            sx={{ borderRadius: 2 }}
            action={
              <Button color="inherit" size="small" onClick={clearActiveSubset}>
                Clear selection
              </Button>
            }>
            <Typography variant="body2" fontWeight={800}>
              Property filter
            </Typography>
            <Typography variant="body2">
              Showing data for a subset of properties ({activePropertyIds.length.toLocaleString()} out of{' '}
              {numberOfProperties?.toLocaleString() ?? '—'}).
            </Typography>
          </Alert>
        )}

        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <PortfolioSummaryMetricCard title="Average Risk Rating" transitionKey={kpiMotionKey}>
              <LoadingValue loading={scoreLoading} variant="rounded" width={72} height={20}>
                <ExtremeWindScoreBadge score={kpis.avgScore} size="dense" />
              </LoadingValue>
            </PortfolioSummaryMetricCard>
          </Grid>

          <Grid item xs={12} md={4}>
            <PortfolioSummaryMetricCard title="Total Damage Loss" transitionKey={kpiMotionKey}>
              <LoadingValue loading={lossLoading} variant="rounded" width={140} height={24}>
                <Chip label={kpis.totalLoss != null ? `${currencySymbol}${compactNumber(kpis.totalLoss)} / year` : '—'} />
              </LoadingValue>
            </PortfolioSummaryMetricCard>
          </Grid>

          <Grid item xs={12} md={4}>
            <PortfolioSummaryMetricCard title="Average Business Interruption" transitionKey={kpiMotionKey}>
              <LoadingValue loading={biLoading} variant="rounded" width={120} height={24}>
                <Chip label={kpis.avgBiDays != null ? `${fixed1(kpis.avgBiDays)} days/year` : '—'} />
              </LoadingValue>
            </PortfolioSummaryMetricCard>
          </Grid>
        </Grid>

        <PortfolioPropertyMetricMap
          title="Portfolio Map"
          height={600}
          selectedPeriod={selectedPeriod}
          colorOptions={mapColorOptions}
          radiusOptions={mapRadiusOptions}
          defaultColorKey="score"
          defaultRadiusKey="structure_value"
          popupFields={mapPopupFields}
          onViewProperty={goToProperty}
          onConfirmSelection={handleConfirmSelection}
          onClearSelection={handleClearSelection}
          clearSelectionToken={mapClearSelectionToken}
        />

        <PortfolioPropertyBreakdownTable
          metrics={tableMetrics}
          selectedPeriod={selectedPeriod}
          filterPropertyIds={activePropertyIds}
          onViewProperty={goToProperty}
          height={680}
          initialSort={{ metricKey: 'score', sort: 'desc' }}
        />
      </Stack>
    </Stack>
  )
}
